import { Injectable } from '@angular/core';
import { toast } from 'ngx-sonner';

/**
 * Toast notification service using ngx-sonner
 * Provides elegant, accessible toast notifications with beautiful animations
 * 
 * @example
 * ```typescript
 * constructor(private toastService: ToastService) {}
 * 
 * this.toastService.success('Operation completed successfully!');
 * this.toastService.error('An error occurred');
 * this.toastService.warning('Please check your input');
 * this.toastService.info('New message received');
 * ```
 */
@Injectable({
    providedIn: 'root'
})
export class ToastService {

    /**
     * Show a success toast notification
     * @param detail - Main message content
     * @param summary - Optional title/summary (shown above detail)
     * @param onClick - Optional callback when toast is clicked
     */
    success(detail: string, summary?: string, onClick?: () => void) {
        const message = summary ? `${summary}\n${detail}` : detail;
        toast.success(message, {
            duration: 3000,
            closeButton: true,
            action: onClick ? {
                label: 'Action',
                onClick: onClick
            } : undefined
        });
    }

    /**
     * Show an error toast notification
     * @param detail - Main message content
     * @param summary - Optional title/summary (shown above detail)
     * @param onClick - Optional callback when toast is clicked
     */
    error(detail: string, summary?: string, onClick?: () => void) {
        const message = summary ? `${summary}\n${detail}` : detail;
        toast.error(message, {
            duration: 5000, // Errors stay longer
            closeButton: true,
            action: onClick ? {
                label: 'Action',
                onClick: onClick
            } : undefined
        });
    }

    /**
     * Show a warning toast notification
     * @param detail - Main message content
     * @param summary - Optional title/summary (shown above detail)
     * @param onClick - Optional callback when toast is clicked
     */
    warn(detail: string, summary?: string, onClick?: () => void) {
        const message = summary ? `${summary}\n${detail}` : detail;
        toast.warning(message, {
            duration: 4000,
            closeButton: true,
            action: onClick ? {
                label: 'Action',
                onClick: onClick
            } : undefined
        });
    }

    /**
     * Show an info toast notification
     * @param detail - Main message content
     * @param summary - Optional title/summary (shown above detail)
     * @param onClick - Optional callback when toast is clicked
     */
    info(detail: string, summary?: string, onClick?: () => void) {
        const message = summary ? `${summary}\n${detail}` : detail;
        toast.info(message, {
            duration: 3000,
            closeButton: true,
            action: onClick ? {
                label: 'Action',
                onClick: onClick
            } : undefined
        });
    }

    /**
     * Show a loading toast notification
     * Returns a function to dismiss the toast
     * @param message - Loading message
     * @returns Function to dismiss the toast
     */
    loading(message: string = 'Loading...') {
        const toastId = toast.loading(message);
        return () => toast.dismiss(toastId);
    }

    /**
     * Show a promise toast that updates based on promise state
     * @param promise - Promise to track
     * @param messages - Messages for loading, success, and error states
     */
    promise<T>(
        promise: Promise<T>,
        messages: {
            loading: string;
            success: string | ((data: T) => string);
            error: string | ((error: any) => string);
        }
    ) {
        return toast.promise(promise, messages);
    }

    /**
     * Dismiss all active toasts
     */
    dismissAll() {
        toast.dismiss();
    }

    /**
     * Show a custom toast with full control
     * @param message - Message to display
     * @param options - Custom toast options
     */
    custom(message: string, options?: any) {
        return toast(message, options);
    }
}
